import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Link,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material";
import Autocomplete, {
  AutocompleteInputChangeReason,
} from "@mui/material/Autocomplete";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { uniq } from "lodash";

import { ReactComponent as PartnerFusionSvg } from "assets/images/icons/partners_fusion.svg";
import GeneViewerIcon from "assets/images/icons/sample_analysis.svg";
import { DESIGNS } from "helpers/apis";
import useAxios from "helpers/axios";
import { CUSTOMER_SUPPORT_EMAIL } from "helpers/constants";
import useCatalogDetail from "hooks/useCatalogDetail";
import { colors } from "theme";
import { IDesign } from "types/design";

interface IErrorContent {
  errorCode: ERROR_CODE;
  title: string;
  description: (coveredPartners?: string[]) => string | React.ReactNode;
}

enum ERROR_CODE {
  NO_ERROR,
  TARGET_ALREADY_EXIST,
  GENE_NOT_FOUND,
  GENE_CONTENT_NOT_FOUND,
  NO_NONINTERACTING_DESIGN_EXISTS,
  GENE_NO_CONTENT_SOME_TARGETS,
  GENE_NO_CONTENT_SOME_PARTNERS,
}

const ERROR_CONTENTS: IErrorContent[] = [
  {
    errorCode: ERROR_CODE.TARGET_ALREADY_EXIST,
    title: "Target is already included in the design.",
    description: () => "Please try another search.",
  },
  {
    errorCode: ERROR_CODE.GENE_NOT_FOUND,
    title: "The entered target is not a valid HGNC symbol.",
    description: () =>
      "Please check the spelling or use a more general term and search again.",
  },
  {
    errorCode: ERROR_CODE.GENE_CONTENT_NOT_FOUND,
    title: "No predesigned content is available for this target.",
    description: () => (
      <Box component={"span"}>
        <Link
          color="secondary"
          target="_blank"
          onMouseDown={() =>
            window.open(`mailto:${CUSTOMER_SUPPORT_EMAIL}`, "_blank")
          }
        >
          Request a consultation
        </Link>{" "}
        with the Custom Assay Design team.
      </Box>
    ),
  },
  {
    errorCode: ERROR_CODE.NO_NONINTERACTING_DESIGN_EXISTS,
    title: "No gene level module exists for this target.",
    description: () =>
      "However, it is targeted in the context of a catalog panel. Upload a full gene list using Upload Targets to see optimal suggested designs.",
  },
  {
    errorCode: ERROR_CODE.GENE_NO_CONTENT_SOME_TARGETS,
    title: "No additional content exists for this target.",
    description: () =>
      "However, one or more queried targets are already included in the design.",
  },
  {
    errorCode: ERROR_CODE.GENE_NO_CONTENT_SOME_PARTNERS,
    title: "No predesigned content exists for this target.",
    description: (coveredPartners: string[] = []) =>
      `However, the following known fusion partner${
        coveredPartners.length > 1 ? "s" : ""
      } of the queried target are already included in the design: ${coveredPartners.join(
        ", "
      )}.`,
  },
];

const useStyles = makeStyles(() => ({
  filledInputRoot: {
    padding: "16px 12px !important",
    "& > input": {
      padding: "0 4px !important",
    },
    "& > svg": {
      fontSize: 18,
    },
  },
  focusedFilledInputRoot: {
    padding: "21px 100px 4px 12px !important",
  },
  endAdornment: {
    position: "absolute",
    top: 9,
    bottom: 9,
    right: 12,
    "& > button": {
      color: colors.grey[300],
    },
    "& > hr": {
      color: colors.grey[400],
      marginRight: 6,
      marginLeft: 12,
    },
  },
  closeBtn: {
    display: "flex",
  },
  searchResultContainer: {
    boxShadow: "0px 8px 10px rgba(0, 0, 0, 0.14)",
    borderRadius: 4,
    position: "absolute",
    left: 0,
    right: 0,
    zIndex: 10,
  },
  searchHint: {
    fontSize: 12,
    lineHeight: "16px",
    letterSpacing: 0.5,
    fontStyle: "italic",
    color: colors.semantic.neutral,
  },
  notFoundContent: {
    "& h4": {
      letterSpacing: 0.25,
    },
    "& > h6": {
      letterSpacing: 0.1,
      fontWeight: 400,
    },
  },
  listofPanels: {
    margin: "0 -16px",
    "&::-webkit-scrollbar": {
      width: 8,
      border: "1px solid #C9CBD5",
    },
    "&::-webkit-scrollbar-thumb": {
      border: "1px solid #FFF",
      background: colors.grey[500],
    },
    "& .MuiListItem-root": {
      padding: "7px 16px",
    },
    "& .MuiTypography-root": {
      fontSize: "12px !important",
      lineHeight: "18px !important",
      letterSpacing: "0.5px !important",
    },
  },
  moduleDetailInfo: {
    color: colors.grey[300],
  },
}));

const ErrorContentBox: React.FC<{
  errorCode: ERROR_CODE;
  coveredPartners: string[];
}> = ({ errorCode, coveredPartners }) => {
  const classes = useStyles();
  const errorContent = ERROR_CONTENTS.find(
    (content) => content.errorCode === errorCode
  );

  if (errorContent) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        mt={1.5}
        mb={6}
        className={classes.notFoundContent}
      >
        <img
          src={GeneViewerIcon}
          width={60}
          height={60}
          alt="Gene Viewer Icon"
        />
        <Box mb={1} mt={2}>
          <Typography variant="h4" align="center">
            {errorContent.title}
          </Typography>
        </Box>
        <Typography variant="subtitle2" align="center">
          {errorContent.description(coveredPartners)}
        </Typography>
      </Box>
    );
  } else {
    return <></>;
  }
};

const SearchTargetDropdown = () => {
  const [inputValue, setInputValue] = useState<string>("");
  const [isInputFocused, setInputFocused] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<ERROR_CODE>(ERROR_CODE.NO_ERROR); // A flag to identify if an enter is hit
  const [coveredPartners, setCoveredPartners] = useState<string[]>([]);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(
    null
  );
  const [options, setOptions] = useState<IDesign[]>([]);
  const classes = useStyles();
  const {
    catalog,
    draftAlertStatus,
    newDetails,
    fetchDesignDetails,
    isSuggestedPanel,
    workflow,
    diseaseType,
    setDraftAlertStatus,
    setFoundGenes,
  } = useCatalogDetail();

  const { fetchData, loading } = useAxios({
    config: {
      url: DESIGNS,
    },
    callOnInit: false,
    successCallback: ({ response }: any) => {
      if (Array.isArray(response?.data)) {
        setOptions(response.data);
      } else {
        setOptions([]);
      }
      if (response.errorCode) {
        setErrorCode(response.errorCode);
        setCoveredPartners(
          (response.preexistingCoverage?.coveredPartners || []).sort()
        );
      } else {
        setErrorCode(ERROR_CODE.NO_ERROR);
      }
    },
  });

  useEffect(() => {
    if (!isInputFocused) {
      setOptions([]);
      setSelectedOptionIndex(null);
      setErrorCode(ERROR_CODE.NO_ERROR);
    }
  }, [isInputFocused]);

  useEffect(() => {
    setOptions([]);
    setErrorCode(ERROR_CODE.NO_ERROR);
  }, [inputValue]);

  const searchResults = () => {
    const panels = [
      ...(isSuggestedPanel ? [] : [(catalog as IDesign).id]),
      ...newDetails.keys(),
    ];
    fetchData({
      params: {
        ...(panels.length > 0 && { panelspresent: panels.join() }),
        targets: inputValue,
        workflow: workflow?.id,
        diseaseType: diseaseType?.id,
      },
    });
  };

  const onPanelSelection = (panel: IDesign) => {
    setInputValue("");
    setTimeout(() => {
      setInputFocused(true);
      setSelectedOptionIndex(null);
    }, 0);
    fetchDesignDetails([{ panelId: panel.id }]);
    // Update found genes for Known Fusion Partner
    setFoundGenes((foundGenes: string) =>
      uniq([...foundGenes.split(","), ...panel.coveredGenes]).join(",")
    );
  };

  return (
    <Autocomplete
      id="find_module_dropdown"
      disableCloseOnSelect
      disableClearable
      inputValue={inputValue}
      popupIcon={null}
      forcePopupIcon={false}
      openOnFocus
      onKeyPress={(event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
          event.preventDefault();
          searchResults();
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Search targets"
          variant="filled"
          onKeyDown={(event: React.KeyboardEvent) => {
            if (errorCode === ERROR_CODE.NO_ERROR && options.length > 0) {
              switch (event.key) {
                case "ArrowUp":
                  setSelectedOptionIndex(
                    selectedOptionIndex === null
                      ? options.length - 1
                      : Math.max(0, selectedOptionIndex - 1)
                  );
                  break;
                case "ArrowDown":
                  setSelectedOptionIndex(
                    selectedOptionIndex === null
                      ? 0
                      : Math.min(options.length - 1, selectedOptionIndex + 1)
                  );
                  break;
                case "Enter":
                  if (selectedOptionIndex !== null) {
                    event.preventDefault();
                    onPanelSelection(options[selectedOptionIndex]);
                  }
                  break;
              }
            }
          }}
          InputProps={{
            ...params.InputProps,
            classes: {
              root: classes.filledInputRoot,
              focused: isInputFocused ? classes.focusedFilledInputRoot : "",
            },
            endAdornment: isInputFocused ? (
              <Box
                display="flex"
                className={classes.endAdornment}
                alignItems="center"
              >
                {!loading && (
                  <Link
                    component="button"
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      event.preventDefault();
                      setInputValue("");
                    }}
                  >
                    Clear
                  </Link>
                )}
                {loading && <CircularProgress color="inherit" size={20} />}
                <Divider orientation="vertical" flexItem />
                <Link
                  component="button"
                  onClick={(event: React.MouseEvent<HTMLElement>) => {
                    event.preventDefault();
                    setInputValue("");
                    setInputFocused(false);
                  }}
                  className={classes.closeBtn}
                >
                  <CloseIcon color="primary" />
                </Link>
              </Box>
            ) : (
              <SearchIcon color="primary" />
            ),
          }}
          inputRef={(input) => {
            if (input) {
              isInputFocused ? input.focus() : input.blur();
            }
          }}
        />
      )}
      options={[]}
      onInputChange={(
        event: React.ChangeEvent<{}>,
        newValue: string,
        reason: AutocompleteInputChangeReason
      ) => {
        setInputValue(newValue);
      }}
      PopperComponent={() => {
        if (!isInputFocused) return null;
        return (
          <Box
            bgcolor="#FFF"
            px={2}
            pt={0.25}
            py={1}
            className={classes.searchResultContainer}
          >
            <Typography className={classes.searchHint}>
              {loading
                ? "Loading..."
                : "Hit enter to get results or esc to clear"}
            </Typography>
            {!loading && (
              <ErrorContentBox
                errorCode={errorCode}
                coveredPartners={coveredPartners}
              />
            )}
            {errorCode === ERROR_CODE.NO_ERROR && options.length > 0 && (
              <FixedSizeList
                width="calc(100% + 32px)"
                height={200}
                itemSize={48}
                itemCount={options.length}
                className={classes.listofPanels}
              >
                {({ index, style }: ListChildComponentProps) => (
                  <ListItem
                    button
                    selected={index === selectedOptionIndex}
                    style={style}
                    key={options[index].id}
                    onMouseDown={() => onPanelSelection(options[index])}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography>{options[index].name}</Typography>
                          {options[index].numberCoveredPartnerGenes > 0 && (
                            <PartnerFusionSvg width={24} height={24} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography className={classes.moduleDetailInfo}>
                          Genes: {options[index].numberGenes} Reads:{" "}
                          {options[index].readsRecommended}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </FixedSizeList>
            )}
          </Box>
        );
      }}
      onClose={() => setInputFocused(false)}
      onOpen={() => {
        setInputFocused(true);
        // Close any message opened
        if (draftAlertStatus) {
          setDraftAlertStatus(null);
        }
      }}
    />
  );
};

export default SearchTargetDropdown;
